import React from 'react';
import { createPortal } from 'react-dom';

import { useTourGuide } from '../TourGuideProvider';
import { placeTaskBar } from './barPlacement';
import { modalSafeChromeProps } from './modalSafe';

const STEP_OPENING_LABEL = 'Opening the next screen…';

export function TaskFlowTracker() {
  const tour = useTourGuide();
  const listRef = React.useRef<HTMLOListElement | null>(null);
  const [viewport, setViewport] = React.useState(() => ({
    width: typeof window === 'undefined' ? 0 : window.innerWidth,
    height: typeof window === 'undefined' ? 0 : window.innerHeight,
  }));

  React.useEffect(() => {
    const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const task = tour?.task;
  const activeId = task?.step?.id ?? null;

  React.useEffect(() => {
    if (!activeId || !listRef.current) return;
    const chip = listRef.current.querySelector(`[data-step="${CSS.escape(activeId)}"]`);
    chip?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [activeId]);

  if (!task?.running) return null;

  const placement = placeTaskBar(task.avoid, viewport, task.target);

  const opening = task.preparing || Boolean(task.step?.awaiting);

  return createPortal(
    <aside
      className="tour-task-bar"
      data-testid="tour-task-bar"
      data-state={task.preparing ? 'preparing' : task.finished ? 'finished' : 'running'}
      data-dodge={placement.dodge}
      data-compact={placement.compact ? 'true' : 'false'}
      data-lifted={placement.lift > 0 ? 'true' : 'false'}
      style={
        {
          '--tour-bar-inset': `${placement.inset}px`,
          '--tour-bar-max': `${placement.maxWidth}px`,
          '--tour-bar-lift': `${placement.lift}px`,
        } as React.CSSProperties
      }
      role="status"
      aria-live="polite"
      aria-label={
        task.preparing
          ? `${task.title}: ${task.preparingLabel}`
          : `${task.title}: step ${Math.min(task.index + 1, task.total)} of ${task.total}`
      }
      {...modalSafeChromeProps}
    >
      <div className="tour-task-bar__head">
        <span className="tour-task-bar__title">{task.title}</span>
        <span className="tour-task-bar__sub">
          {task.preparing
            ? 'Getting ready'
            : task.finished
              ? 'Finished'
              : `${Math.min(task.index + 1, task.total)} / ${task.total}`}
        </span>
      </div>

      {opening ? (
        <div className="tour-task-bar__preparing" data-testid="tour-task-preparing">
          <span className="tour-task-bar__spinner" aria-hidden="true" />
          <span className="tour-task-bar__preparingtext">
            {task.preparing ? task.preparingLabel : STEP_OPENING_LABEL}
          </span>
        </div>
      ) : task.finished ? (
        <div className="tour-task-bar__done" data-testid="tour-task-done">
          <div className="tour-task-bar__donetext">
            <strong>{task.doneTitle || task.title}</strong>
            <span>
              {task.skippedCount > 0
                ? `Reached the end — ${task.skippedCount} step${task.skippedCount === 1 ? '' : 's'} skipped, so this may not have been created.`
                : task.doneDescription || 'Everything on the checklist is done.'}
            </span>
          </div>
          {task.whenDone !== 'panel' ? (
            <div className="tour-task-bar__preparing" data-testid="tour-task-handoff">
              <span className="tour-task-bar__spinner" aria-hidden="true" />
              <span className="tour-task-bar__preparingtext">
                {task.whenDone === 'close' ? 'Closing the tour…' : 'Back to the tour…'}
              </span>
            </div>
          ) : (
            <button
              type="button"
              className="tour-task-bar__cta"
              onClick={() => tour?.endTask('step')}
              data-testid="tour-task-resume"
            >
              Back to the tour
            </button>
          )}
        </div>
      ) : (
        <ol className="tour-task-bar__list" ref={listRef}>
          {task.steps.map((step, i) => (
            <li
              key={step.id}
              className="tour-task-bar__item"
              data-state={step.state}
              data-step={step.id}
              data-testid={`tour-task-step-${step.id}`}
              aria-current={step.state === 'active' ? 'step' : undefined}
            >
              <span className="tour-task-bar__marker" aria-hidden="true">
                {step.state === 'complete' ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : step.state === 'skipped' ? (
                  '–'
                ) : (
                  i + 1
                )}
              </span>
              <span className="tour-task-bar__label">{step.label}</span>
              {i < task.steps.length - 1 && (
                <span className="tour-task-bar__connector" aria-hidden="true" />
              )}
            </li>
          ))}
        </ol>
      )}

      {!task.finished && (
        <button
          type="button"
          className="tour-task-bar__close"
          onClick={() => tour?.endTask()}
          aria-label="Leave this walkthrough and go back to the tour"
          title="Leave this walkthrough"
          data-testid="tour-task-exit"
        >
          ×
        </button>
      )}
    </aside>,
    document.body,
  );
}
